import { useCallback, useMemo } from 'react';
import type { AddressType } from '@sg/garnish';

import { useCartQuery } from '@order/graphql';

import { useCartMachine } from './Cart.machine';
import { getCartLedgerTotal, getCartLineItemsCount } from './useCart.utils';

// ────────────────────────────────────────────────────────────────────────────────

export const useCart = () => {
  const [cartResponse, getCart] = useCartQuery({
    requestPolicy: 'cache-only',
  });
  const {
    shouldDisableCartControls,
    pendingCartMutations,
    executeCartMutation,
  } = useCartMachine();

  const cart = cartResponse.data?.cart;
  const isFetchingCart = cartResponse.fetching || shouldDisableCartControls;

  // ─────────────────────────────────────────────────────────────────

  const { restaurant, lineItems, ledger, deliveryOrderDetail } = cart ?? {};
  const { isOutpost } = restaurant ?? {};

  const lineItemsCount = getCartLineItemsCount(lineItems);
  const orderType = cart?.orderType?.toLowerCase();
  const cartOrderType: AddressType = (
    isOutpost ? 'outpost' : orderType ?? 'pickup'
  ) as AddressType;
  const isDeliveryOrder = cartOrderType === 'delivery';
  const isCartEmpty = lineItemsCount === 0;
  const total = getCartLedgerTotal(ledger);

  const deliveryDetails = useMemo(() => {
    const address = deliveryOrderDetail?.address ?? '';
    const cartSubtotal = ledger?.subtotal ?? 0;
    const minSubtotal = restaurant?.deliveryMinSubtotal ?? 0;
    const hasNotMetDeliveryMinimum =
      isDeliveryOrder && cartSubtotal < minSubtotal;

    return {
      address,
      minSubtotal,
      hasNotMetDeliveryMinimum,
    };
  }, [
    deliveryOrderDetail?.address,
    isDeliveryOrder,
    ledger?.subtotal,
    restaurant?.deliveryMinSubtotal,
  ]);

  // ─── HELPERS ─────────────────────────────────────────────────────

  const fetchCart = useCallback(() => {
    getCart({ requestPolicy: 'cache-and-network' });
  }, [getCart]);

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      cart,
      fetchCart,
      isFetchingCart,
      isCartEmpty,
      cartOrderType,
      lineItemsCount,
      total,
      deliveryDetails,
      pendingCartMutations,
      executeCartMutation,
    }),
    [
      cart,
      fetchCart,
      isFetchingCart,
      isCartEmpty,
      cartOrderType,
      lineItemsCount,
      total,
      deliveryDetails,
      pendingCartMutations,
      executeCartMutation,
    ],
  );
};
